import { useState } from 'react';
import Image from 'next/image';
import PropTypes from 'prop-types';
import Dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useUser } from '@auth0/nextjs-auth0/client';
import { getCookie } from 'cookies-next';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';

import {
  DefaultMenuNav,
  DefaultTabs,
  AccountDefaultMenuNav,
} from 'mocks/menuNav';

import { usePermissions } from 'providers/permissions';

import { isSalesExecutiveSession } from 'utils/auth/authHelper';

const Logo = Dynamic(() => import('components/Logo'), {
  ssr: false,
  loading: () => <Box />,
});

const SideMenuComponent = Dynamic(
  () => import('components/Shared/Header/SideMenu'),
  {
    ssr: true,
  }
);

const TableTabs = Dynamic(() => import('components/Shared/Header/Tabs'), {
  ssr: false,
  loading: () => (
    <Box display="flex" flexDirection="row" gap={3} px={3}>
      <Skeleton height={48} width="100%" />

      <Skeleton height={48} width="100%" />
    </Box>
  ),
});

const ExchangeRateModalComponent = Dynamic(
  () =>
    import(
      'components/SalesExecutivesComponents/ExchangeRateModalComponent/ModalComponent'
    ),
  { ssr: false }
);

Header.propTypes = {
  eduBasicLogo: PropTypes.bool,
  logoLink: PropTypes.string,
  menuNavHeader: PropTypes.instanceOf(Array),
  menuNavSideMenu: PropTypes.instanceOf(Array),
  showMenu: PropTypes.bool,
  showTabs: PropTypes.bool,
  tabs: PropTypes.instanceOf(Array),
};

export default function Header({
  eduBasicLogo = false,
  logoLink = '/',
  menuNavHeader = DefaultMenuNav,
  menuNavSideMenu = AccountDefaultMenuNav,
  showMenu = true,
  showTabs = true,
  tabs = DefaultTabs,
}) {
  const { user } = useUser();
  const theme = useTheme();
  const router = useRouter();
  const mobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const isSalesExecutives = user ? isSalesExecutiveSession(user) : false;
  const permissions = usePermissions(user);
  const [openModal, setOpenModal] = useState(false);

  const runningInWebViewCookie = getCookie('runningInWebView', {});

  const isRunningInWebView = runningInWebViewCookie?.toLowerCase() === 'true';

  const edubasicRegex = /EduBasic\/Auth/;
  const isEdubasicLogin = edubasicRegex.test(router.pathname);

  return (
    <AppBar
      id="header"
      position="sticky"
      color="inherit"
      elevation={1}
      sx={{ pb: 0.5 }}
    >
      {mobileBreakpoint &&
        !permissions.isKids &&
        !isEdubasicLogin &&
        !isRunningInWebView &&
        showTabs && <TableTabs tabs={tabs} />}

      <Toolbar>
        <Box
          alignItems="center"
          display="flex"
          flexGrow={1}
          justifyContent="space-between"
        >
          {eduBasicLogo ? (
            <Image
              loader={({ src, width }) => `${src}?w=${width}`}
              alt="Fluencypass for schools"
              height={60}
              priority
              quality={50}
              src="https://res.cloudinary.com/intercambiodireto-com/image/upload/v1745355501/Landing%20Pages/Bett/logo_ov41zu.png"
              width={260}
            />
          ) : (
            <Logo link={logoLink} logoType="minimalOnMobile" />
          )}

          {showMenu && (
            <>
              <Box>
                <SideMenuComponent
                  menuNavHeader={menuNavHeader}
                  menuNavSideMenu={menuNavSideMenu}
                  permissions={{ ...permissions, isSalesExecutives }}
                  setOpenModal={setOpenModal}
                  user={user}
                />
              </Box>

              {isSalesExecutives && (
                <ExchangeRateModalComponent
                  openModal={openModal}
                  setOpenModal={setOpenModal}
                />
              )}
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}
